package ai.jessie.web.projects

import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.web.theme.BadgeTone
import ai.jessie.web.theme.CalendarScrollStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.HeadTableRowVariant
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.ProjectTableRowStyle
import ai.jessie.web.theme.ProjectTableStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableIconStyle
import ai.jessie.web.theme.TableNumStyle
import ai.jessie.web.theme.TableRowKind
import ai.jessie.web.theme.TableRowStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.TableStyle
import ai.jessie.web.theme.badge
import ai.jessie.web.theme.icon
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyleVariant
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

// Step 2, A12: only the projects the server returned for this organisation, in its order, with
// their customer, active state and positions (`docs/design/screens/12-Projekte.png`).
@Composable
internal fun projectList(organisationId: String, state: ProjectUiState, actions: ProjectActions) {
    val projects = state.projects
    if (projects != null) {
        if (projects.isEmpty()) P(attrs = HintStyle.toAttrs()) { Text("Noch keine Projekte.") }
        else projectTable(organisationId, projects)
    }
    Div(attrs = RowStyle.toAttrs()) {
        themedButton("Projektliste erneut laden", actions.refresh, disabled = state.busy)
    }
}

@Composable
private fun projectTable(organisationId: String, projects: List<ProjectResponse>) {
    Div(attrs = CardStyle.toAttrs()) {
        Div(attrs = CalendarScrollStyle.toAttrs()) {
            Div(
                attrs =
                    TableStyle.toModifier().then(ProjectTableStyle.toModifier()).toAttrs {
                        attr("role", "table")
                        attr("aria-label", PROJECTS_TITLE)
                    }
            ) {
                projectRow(HeadTableRowVariant) {
                    listOf("Projekt", "Kunde", "Status", "Positionen", "").forEach {
                        Span(attrs = { attr("role", "columnheader") }) { Text(it) }
                    }
                }
                projects.forEach { projectEntryRow(organisationId, it) }
            }
        }
    }
}

@Composable
private fun projectEntryRow(organisationId: String, project: ProjectResponse) {
    val href = "/organisations/$organisationId/projects/${project.id}"
    projectRow(null) {
        Span(attrs = { attr("role", "cell") }) {
            A(href = href, attrs = TableStrongStyle.toAttrs()) { Text(project.name) }
            if (project.positions.isNotEmpty())
                Ul(attrs = listOf(SubListStyle, SmallStyle, MutedStyle).toAttrs()) {
                    project.positions.forEach { Li { Text(it.summary()) } }
                }
        }
        Span(attrs = { attr("role", "cell") }) { Text(project.customerName ?: "Kein Kunde") }
        Span(attrs = { attr("role", "cell") }) { activeBadge(project.active) }
        Span(attrs = TableNumStyle.toAttrs { attr("role", "cell") }) {
            Text(project.positions.size.toString())
        }
        Span(attrs = TableIconStyle.toAttrs { attr("role", "cell") }) {
            A(href = href, attrs = { attr("aria-label", "${project.name} öffnen") }) {
                icon("chevron-right", size = 12)
            }
        }
    }
}

@Composable
private fun projectRow(variant: CssStyleVariant<TableRowKind>?, cells: @Composable () -> Unit) {
    Div(
        attrs =
            TableRowStyle.toModifier(variant).then(ProjectTableRowStyle.toModifier()).toAttrs {
                attr("role", "row")
            }
    ) {
        cells()
    }
}

/** Aktiv in the hours tint (`05-Projekt.png`), Inaktiv neutral. */
@Composable
internal fun activeBadge(active: Boolean) {
    badge(activeLabel(active), if (active) BadgeTone.HOURS else BadgeTone.NEUTRAL)
}

/** Verrechenbar in the hours tint (`05-Projekt.png`), Nicht verrechenbar neutral. */
@Composable
internal fun billableBadge(billable: Boolean) {
    badge(billableLabel(billable), if (billable) BadgeTone.HOURS else BadgeTone.NEUTRAL)
}
